import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Info } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { FormInput } from "@/components/shared/FormInput";
import { PrimaryButton } from "@/components/shared/Buttons";
import { useSeo } from "@/hooks/useSeo";
import {
  getPayConfig, getPurposes, createOrder, verifyPayment, markFailed,
  loadRazorpayScript, isValidIndianMobile, isValidEmail,
} from "@/lib/paymentApi";
import { PROGRAMS_DATA } from "@/lib/programsData";

const initial = { purpose: "counselling", program: "", name: "", mobile: "", email: "", referenceNumber: "", amount: "" };

export default function Payment() {
  useSeo("Pay Fees", "Securely pay ASAI counselling, registration, admission or internship fees via Razorpay (UPI, cards, net banking, wallets).");
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [purposes, setPurposes] = useState([]);
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    getPayConfig().then(setCfg).catch(() => setCfg({ enabled: false }));
    getPurposes().then(setPurposes).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const selected = purposes.find((p) => p.key === form.purpose);
  const isCustom = form.purpose === "custom";
  const displayAmount = isCustom ? (form.amount ? Number(form.amount) : 0) : (selected?.amount || 0);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required.";
    if (!isValidIndianMobile(form.mobile)) e.mobile = "Enter a valid 10-digit Indian mobile number.";
    if (!isValidEmail(form.email)) e.email = "Enter a valid email address.";
    if (isCustom && (!form.amount || Number(form.amount) < 1)) e.amount = "Enter a valid amount.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const pay = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setProcessing(true);
    try {
      const order = await createOrder({
        purpose: form.purpose,
        name: form.name,
        mobile: form.mobile,
        email: form.email,
        program: form.program,
        referenceNumber: form.referenceNumber,
        amount: isCustom ? Number(form.amount) : undefined,
      });
      await loadRazorpayScript();
      const rzp = new window.Razorpay({
        key: order.keyId,
        order_id: order.orderId,
        amount: order.amount,
        currency: order.currency,
        name: "ASAI – Atmasamman School of AI",
        description: order.description,
        prefill: { name: order.name, email: order.email, contact: order.contact },
        theme: { color: "#FF5A00" },
        handler: async (resp) => {
          try {
            await verifyPayment({
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            nav(`/payment/success?ref=${order.paymentRef}`);
          } catch (verr) {
            toast.error(verr.message);
            nav(`/payment/failed?ref=${order.paymentRef}`);
          }
        },
        modal: {
          ondismiss: async () => {
            await markFailed(order.orderId, "Checkout dismissed by user");
            setProcessing(false);
            toast.message("Payment cancelled.");
          },
        },
      });
      rzp.on("payment.failed", async (r) => {
        await markFailed(order.orderId, r?.error?.description || "Payment failed");
        nav(`/payment/failed?ref=${order.paymentRef}`);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.message || "Could not start payment.");
      setProcessing(false);
    }
  };

  return (
    <div data-testid="page-payment">
      <PageHeader overline="Payments" titleLines={["Pay your", "ASAI fees securely"]} description="Fast, secure payments powered by Razorpay — UPI, cards, net banking and wallets supported." />

      <section className="bg-white py-20 md:py-28">
        <div className="container-asai grid gap-10 lg:grid-cols-[1.1fr_0.9fr]">
          <form onSubmit={pay} noValidate data-testid="payment-form" className="flex flex-col gap-5 rounded-3xl border border-navy/10 bg-lightgrey p-6 sm:p-8">
            {cfg && !cfg.enabled && (
              <div className="flex items-start gap-3 rounded-xl border border-orange/30 bg-orange/10 p-4" data-testid="payment-disabled">
                <Info className="mt-0.5 h-5 w-5 shrink-0 text-orange" />
                <p className="text-sm text-navy/80">Online payments are not yet configured. Please check back soon or contact our team.</p>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-navy/70">Payment Purpose</label>
              <select value={form.purpose} onChange={set("purpose")} data-testid="payment-purpose" className="w-full rounded-lg border border-navy/15 bg-white px-4 py-3 text-sm text-navy outline-none focus:border-royal">
                {purposes.map((p) => (<option key={p.key} value={p.key}>{p.label}{p.fixed ? ` — ₹${p.amount.toLocaleString("en-IN")}` : ""}</option>))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-navy/70">Related Program (optional)</label>
              <select value={form.program} onChange={set("program")} data-testid="payment-program" className="w-full rounded-lg border border-navy/15 bg-white px-4 py-3 text-sm text-navy outline-none focus:border-royal">
                <option value="">Not applicable</option>
                {PROGRAMS_DATA.map((p) => (<option key={p.slug} value={p.name}>{p.name}</option>))}
              </select>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormInput label="Full Name" id="pay-name" value={form.name} onChange={set("name")} error={errors.name} placeholder="Your name" testId="payment-name" />
              <FormInput label="Mobile Number" id="pay-mobile" value={form.mobile} onChange={set("mobile")} error={errors.mobile} placeholder="10-digit mobile" testId="payment-mobile" />
              <FormInput label="Email Address" id="pay-email" type="email" value={form.email} onChange={set("email")} error={errors.email} placeholder="you@example.com" testId="payment-email" />
              <FormInput label="Application / Reference No. (optional)" id="pay-ref" value={form.referenceNumber} onChange={set("referenceNumber")} placeholder="e.g. ASAI-…" testId="payment-reference" />
            </div>

            {isCustom && (
              <FormInput label="Amount (₹)" id="pay-amount" type="number" value={form.amount} onChange={set("amount")} error={errors.amount} placeholder="Enter amount" testId="payment-amount" />
            )}

            <PrimaryButton type="submit" size="lg" disabled={processing || (cfg && !cfg.enabled)} testId="payment-submit">
              {processing ? <><Loader2 className="h-4 w-4 animate-spin" /> Processing…</> : `Pay ₹${displayAmount.toLocaleString("en-IN")}`}
            </PrimaryButton>
          </form>

          <div className="flex flex-col gap-6">
            <div className="rounded-2xl border border-navy/10 bg-white p-6">
              <div className="flex items-center gap-2 text-navy"><ShieldCheck className="h-5 w-5 text-royal" /> <span className="font-heading text-lg">Secure Payment</span></div>
              <p className="mt-3 text-sm text-navy/65">Payments are processed securely by Razorpay. ASAI never stores your card details. The payable amount is verified on our server.</p>
            </div>
            <div className="rounded-2xl border border-navy/10 bg-white p-6">
              <div className="font-heading text-lg text-navy">Supported methods</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {["UPI", "Credit Card", "Debit Card", "Net Banking", "Wallets"].map((m) => (
                  <span key={m} className="rounded-full border border-navy/12 bg-lightgrey px-3 py-1.5 text-xs font-medium text-navy">{m}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
